/**
 * Tareas — subsección de Leads (`/leads/tareas`).
 *
 * Estaba suelta en `/tasks` y solo sabía crear, filtrar por dos campos y marcar
 * completada: no había forma de editar una tarea ni de borrarla, y la relación
 * con la ficha —el motivo por el que existe una tarea en un CRM— ni se pintaba.
 *
 * La lista va agrupada por vencimiento y no plana porque la pregunta que se le
 * hace es siempre «qué toca ahora», y una lista ordenada por fecha obliga a
 * buscar dónde acaba lo urgente. Los filtros viven en la URL para que un
 * «vencidas de Ana» se pueda guardar en marcadores o mandar por Telegram.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import type { Task } from '@/lib/types';
import { classNames } from '@/lib/utils';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import EmptyState from '@/components/ui/EmptyState';
import FilaTarea from '@/components/tareas/FilaTarea';
import TareaModal, { type DatosTarea } from '@/components/tareas/TareaModal';
import {
  ETIQUETA_FRANJA, ETIQUETA_PRIORIDAD, ETIQUETA_TIPO, ORDEN_FRANJAS,
  atajoFecha, clasificar, type Franja,
} from '@/components/tareas/tipos';
import { Plus, Search, Filter, X, CheckSquare, Loader2 } from 'lucide-react';

/** Filtros que viajan en la URL. Vacío = no aparece en la barra de direcciones. */
const FILTROS = ['q', 'prioridad', 'tipo', 'responsable', 'vencidas', 'cerradas'] as const;
type Filtro = typeof FILTROS[number];

export default function TareasPage() {
  const [params, setParams] = useSearchParams();
  const [tareas, setTareas] = useState<Task[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modal, setModal] = useState<{ tarea: Task | null } | null>(null);
  const [seleccion, setSeleccion] = useState<Set<string>>(new Set());
  const [enLote, setEnLote] = useState(false);
  const [verFiltros, setVerFiltros] = useState(false);
  const [altaRapida, setAltaRapida] = useState('');
  const [creandoRapida, setCreandoRapida] = useState(false);

  const filtro = (f: Filtro) => params.get(f) || '';
  const ponerFiltro = (f: Filtro, valor: string) => {
    const siguientes = new URLSearchParams(params);
    valor ? siguientes.set(f, valor) : siguientes.delete(f);
    setParams(siguientes, { replace: true });
  };
  const hayFiltros = FILTROS.some(f => params.get(f));

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const consulta: Record<string, string> = {};
      if (filtro('q')) consulta.search = filtro('q');
      if (filtro('prioridad')) consulta.priority = filtro('prioridad');
      if (filtro('tipo')) consulta.tipo = filtro('tipo');
      if (filtro('responsable')) consulta.responsable = filtro('responsable');
      if (filtro('vencidas')) consulta.vencidas = 'true';
      if (filtro('cerradas')) consulta.incluirCerradas = 'true';

      setTareas(await api.getTasks(consulta));
    } catch (err: any) {
      setError(err?.message || 'No se pudieron cargar las tareas');
    } finally {
      setCargando(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.toString()]);

  useEffect(() => { cargar(); }, [cargar]);

  // Las tareas ya vistas alimentan las sugerencias de responsable: no hay
  // padrón de usuarios en el CRM y no merece la pena mantener uno para esto.
  const responsables = useMemo(
    () => [...new Set(tareas.map(t => t.responsable).filter((r): r is string => !!r))].sort(),
    [tareas],
  );

  const grupos = useMemo(() => {
    const mapa = new Map<Franja, Task[]>();
    for (const tarea of tareas) {
      const franja = clasificar(tarea);
      const grupo = mapa.get(franja);
      if (grupo) grupo.push(tarea);
      else mapa.set(franja, [tarea]);
    }
    return ORDEN_FRANJAS.filter(f => mapa.has(f)).map(f => ({ franja: f, tareas: mapa.get(f)! }));
  }, [tareas]);

  const alternarSeleccion = (id: string, marcada: boolean) => {
    setSeleccion(prev => {
      const siguiente = new Set(prev);
      marcada ? siguiente.add(id) : siguiente.delete(id);
      return siguiente;
    });
  };

  const enLoteHacer = async (accion: 'completar' | 'eliminar' | 'aplazar', dias?: number) => {
    if (seleccion.size === 0) return;
    if (accion === 'eliminar' && !confirm(`¿Eliminar ${seleccion.size} ${seleccion.size === 1 ? 'tarea' : 'tareas'}?`)) return;

    setEnLote(true);
    try {
      const { fallidas } = await api.bulkTasks([...seleccion], accion, dias);
      if (fallidas.length > 0) alert(`${fallidas.length} no se pudieron actualizar.`);
      setSeleccion(new Set());
      await cargar();
    } finally {
      setEnLote(false);
    }
  };

  const guardar = async (datos: DatosTarea) => {
    if (modal?.tarea) await api.updateTask(modal.tarea.id, datos);
    else await api.createTask(datos);
    setModal(null);
    cargar();
  };

  /**
   * Alta rápida: título y a correr, con vencimiento hoy.
   *
   * La mayoría de las tareas de un CRM nacen en mitad de otra cosa —colgando el
   * teléfono, leyendo un correo— y abrir un modal de nueve campos para escribir
   * seis palabras hace que no se apunten. Lo demás se rellena luego editándola.
   */
  const crearRapida = async (e: React.FormEvent) => {
    e.preventDefault();
    const title = altaRapida.trim();
    if (!title) return;

    setCreandoRapida(true);
    try {
      await api.createTask({ title, dueDate: atajoFecha('hoy'), priority: 'medium', tipo: 'otro' });
      setAltaRapida('');
      await cargar();
    } finally {
      setCreandoRapida(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6 gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold">Tareas</h1>
          <p className="text-slate-500 text-xs sm:text-sm mt-0.5">
            Lo que hay que hacer con los leads, los propietarios y sus alojamientos.
          </p>
        </div>
        <button onClick={() => setModal({ tarea: null })} className="btn-primary flex items-center gap-2 text-sm sm:text-base flex-shrink-0">
          <Plus size={18} /> <span className="hidden sm:inline">Nueva tarea</span><span className="sm:hidden">Nueva</span>
        </button>
      </div>

      <form onSubmit={crearRapida} className="card !p-3 mb-4 flex items-center gap-2">
        <CheckSquare size={16} className="text-sereno-dark/30 flex-shrink-0" />
        <input
          // gris neutro en el marcador de posición, no `text-sereno-dark/30`:
          // las anulaciones de modo oscuro de index.css cubren `text-sereno-dark`
          // pero no su variante `placeholder:`, y quedaba azul marino sobre azul
          // marino, es decir, invisible.
          className="flex-1 min-w-0 bg-transparent text-sm text-sereno-dark focus:outline-none placeholder:text-gray-400"
          placeholder="Apunta una tarea para hoy y pulsa Enter…"
          value={altaRapida}
          onChange={e => setAltaRapida(e.target.value)}
        />
        {altaRapida.trim() && (
          <button type="submit" disabled={creandoRapida} className="btn-primary !py-1 !px-3 text-sm flex-shrink-0">
            {creandoRapida ? <Loader2 size={14} className="animate-spin" /> : 'Añadir'}
          </button>
        )}
      </form>

      <div className="card mb-6 space-y-3">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-sereno-dark/30" />
            <input
              className="input pl-9"
              placeholder="Buscar por título, descripción o ficha…"
              value={filtro('q')}
              onChange={e => ponerFiltro('q', e.target.value)}
            />
          </div>
          <button
            onClick={() => setVerFiltros(v => !v)}
            className={classNames(
              'btn-ghost flex items-center gap-1.5 text-sm flex-shrink-0',
              hayFiltros && 'text-sereno-dark font-semibold',
            )}
          >
            <Filter size={15} /> <span className="hidden sm:inline">Filtros</span>
          </button>
        </div>

        {(verFiltros || hayFiltros) && (
          <div className="flex flex-wrap items-center gap-2">
            <select className="input w-auto" value={filtro('prioridad')} onChange={e => ponerFiltro('prioridad', e.target.value)}>
              <option value="">Toda prioridad</option>
              {Object.entries(ETIQUETA_PRIORIDAD).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>

            <select className="input w-auto" value={filtro('tipo')} onChange={e => ponerFiltro('tipo', e.target.value)}>
              <option value="">Todo tipo</option>
              {Object.entries(ETIQUETA_TIPO).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>

            <select className="input w-auto" value={filtro('responsable')} onChange={e => ponerFiltro('responsable', e.target.value)}>
              <option value="">Cualquier responsable</option>
              {responsables.map(r => <option key={r} value={r}>{r}</option>)}
            </select>

            <button
              onClick={() => ponerFiltro('vencidas', filtro('vencidas') ? '' : 'true')}
              className={classNames(
                'px-3 py-2 text-sm rounded-lg transition-colors',
                filtro('vencidas') ? 'bg-red-500 text-white' : 'bg-gray-100 text-sereno-dark/60 hover:bg-gray-200',
              )}
            >
              Solo vencidas
            </button>

            <button
              onClick={() => ponerFiltro('cerradas', filtro('cerradas') ? '' : 'true')}
              className={classNames(
                'px-3 py-2 text-sm rounded-lg transition-colors',
                filtro('cerradas') ? 'bg-sereno-dark text-white' : 'bg-gray-100 text-sereno-dark/60 hover:bg-gray-200',
              )}
            >
              Ver completadas
            </button>

            {hayFiltros && (
              <button
                onClick={() => setParams(new URLSearchParams(), { replace: true })}
                className="flex items-center gap-1 text-sm text-sereno-dark/40 hover:text-sereno-dark px-2"
              >
                <X size={14} /> Limpiar
              </button>
            )}
          </div>
        )}
      </div>

      {seleccion.size > 0 && (
        // Barra de acciones en lote. Aparece solo con algo seleccionado: si
        // estuviera siempre, ocuparía sitio para no hacer nada la mayor parte
        // del tiempo.
        <div className="card !py-2.5 mb-4 flex items-center gap-3 flex-wrap sticky top-2 z-10 shadow-md">
          <span className="text-sm font-medium">{seleccion.size} seleccionada{seleccion.size === 1 ? '' : 's'}</span>
          <div className="flex items-center gap-2 ml-auto flex-wrap">
            <button disabled={enLote} onClick={() => enLoteHacer('completar')} className="btn-ghost !py-1 text-sm">Completar</button>
            <button disabled={enLote} onClick={() => enLoteHacer('aplazar', 1)} className="btn-ghost !py-1 text-sm">+1 día</button>
            <button disabled={enLote} onClick={() => enLoteHacer('aplazar', 7)} className="btn-ghost !py-1 text-sm">+7 días</button>
            <button disabled={enLote} onClick={() => enLoteHacer('eliminar')} className="btn-ghost !py-1 text-sm text-red-500">Eliminar</button>
            <button onClick={() => setSeleccion(new Set())} className="text-sereno-dark/40 hover:text-sereno-dark p-1"><X size={16} /></button>
          </div>
        </div>
      )}

      {error && <div className="card mb-4 text-sm text-red-500">{error}</div>}

      {cargando ? (
        <LoadingSpinner />
      ) : tareas.length === 0 ? (
        <EmptyState
          title={hayFiltros ? 'Sin tareas con esos filtros' : 'No hay tareas pendientes'}
          description={hayFiltros ? 'Prueba a limpiar los filtros.' : 'Apunta la primera arriba, o créala con el botón.'}
          action={hayFiltros
            ? <button onClick={() => setParams(new URLSearchParams(), { replace: true })} className="btn-primary">Limpiar filtros</button>
            : <button onClick={() => setModal({ tarea: null })} className="btn-primary"><Plus size={16} className="inline mr-1" /> Nueva tarea</button>}
        />
      ) : (
        <div className="space-y-6">
          {grupos.map(({ franja, tareas: delGrupo }) => (
            <section key={franja}>
              <h2 className={classNames(
                'text-xs font-semibold uppercase tracking-wider mb-2 flex items-center gap-2',
                franja === 'vencida' ? 'text-red-500' : 'text-sereno-dark/40',
              )}>
                {ETIQUETA_FRANJA[franja]}
                <span className="font-normal normal-case tracking-normal">({delGrupo.length})</span>
              </h2>
              <div className="space-y-2">
                {delGrupo.map(t => (
                  <FilaTarea
                    key={t.id}
                    tarea={t}
                    seleccionada={seleccion.has(t.id)}
                    onSeleccionar={alternarSeleccion}
                    onCompletar={async tarea => { await api.completeTask(tarea.id); cargar(); }}
                    onReabrir={async tarea => { await api.reopenTask(tarea.id); cargar(); }}
                    onAplazar={async (tarea, dias) => { await api.postponeTask(tarea.id, dias); cargar(); }}
                    onAbrir={tarea => setModal({ tarea })}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      <TareaModal
        abierto={!!modal}
        tarea={modal?.tarea ?? null}
        responsables={responsables}
        onGuardar={guardar}
        onEliminar={modal?.tarea
          ? async () => { await api.deleteTask(modal.tarea!.id); setModal(null); cargar(); }
          : undefined}
        onCerrar={() => setModal(null)}
      />
    </div>
  );
}
